import React, { useEffect, useMemo, useState } from "react";
import {
  createColumns,
  hasAnyChannelEnabled,
} from "../util/UsersSelectionTableFunctions";
import { UsersSelectionTableProps } from "../types/IUsersSelectionTable";
import { IOperationRuleAlertUser } from "../../../../stores/data/insightAlerts/insightAlertModel";
import { useStores } from "../../../../stores/setup/use-store";

export const useUsersSelectionTable = ({
  users,
  selectedKeys,
  onSelectionChange,
  t,
}: UsersSelectionTableProps) => {
  const { dataStores: { authStore } } = useStores();
  const currentUserId = authStore.userId;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [checkAll, setCheckAll] = useState<boolean>(false);

  // Capture the initial selection to sort pre-selected users to the top
  // without re-sorting when the user selects/deselects during the session.
  // Uses state (not ref) so the useMemo recalculates when it's set.
  const [initialSelectedKeys, setInitialSelectedKeys] =
    useState<React.Key[] | null>(null);

  useEffect(() => {
    if (selectedKeys.length > 0) {
      if (initialSelectedKeys === null) {
        // First capture
        setInitialSelectedKeys([...selectedKeys]);
      } else if (selectedKeys.length > initialSelectedKeys.length) {
        // A larger set arrived (e.g., edit drawer synced full recipients
        // after auto-select picked just the current user)
        setInitialSelectedKeys([...selectedKeys]);
      }
    } else if (initialSelectedKeys !== null) {
      // Selection was cleared (e.g., drawer closed and reset) — reset so
      // the next selection round can capture fresh initial keys.
      setInitialSelectedKeys(null);
    }
  }, [selectedKeys, initialSelectedKeys]);

  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (searchTerm.trim()) {
      const searchLower = searchTerm?.toLowerCase()?.trim();
      filtered = filtered?.filter((user) =>
        user?.userName?.toLowerCase().includes(searchLower),
      );
    }

    filtered.sort((a, b) => {
      // 1st: Pre-selected users (from initial load) go to the top
      if (initialSelectedKeys && initialSelectedKeys.length > 0) {
        const aInitial = initialSelectedKeys.includes(a.userId);
        const bInitial = initialSelectedKeys.includes(b.userId);
        if (aInitial && !bInitial) return -1;
        if (!aInitial && bInitial) return 1;
      }

      // 2nd: Users with any channel enabled (or the logged-in user) above disabled users
      const aHasChannel = hasAnyChannelEnabled(a) || a.userId === currentUserId;
      const bHasChannel = hasAnyChannelEnabled(b) || b.userId === currentUserId;

      if (aHasChannel && !bHasChannel) return -1;
      if (!aHasChannel && bHasChannel) return 1;
      return 0;
    });

    return filtered;
  }, [users, searchTerm, initialSelectedKeys, currentUserId]);

  const allUsersWithWhatsApp = useMemo(() => {
    const usersWithAtLeastOneChannel = users?.filter(
      (u) => hasAnyChannelEnabled(u) || u.userId === currentUserId,
    );

    return {
      list: usersWithAtLeastOneChannel,
      hasOnlyUsersWithoutWhatsApp: usersWithAtLeastOneChannel?.length === 0,
    };
  }, [users, currentUserId]);

  useEffect(() => {
    if (
      (selectedKeys?.length === 0 ||
        selectedKeys?.length < allUsersWithWhatsApp?.list?.length) &&
      checkAll
    ) {
      setCheckAll(false);
    } else if (
      selectedKeys?.length >= allUsersWithWhatsApp?.list?.length &&
      !checkAll &&
      selectedKeys?.length !== 0
    ) {
      setCheckAll(true);
    }
  }, [allUsersWithWhatsApp, selectedKeys]);

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      const previouslySelected = selectedKeys?.filter(
        (key) => !filteredUsers?.some((user) => user?.userId === key),
      );

      const allSelectedKeys = [...previouslySelected, ...selectedRowKeys];

      const allSelectedUsers = users?.filter((user) =>
        allSelectedKeys?.includes(user?.userId),
      );

      onSelectionChange(allSelectedKeys, allSelectedUsers);
    },
    getCheckboxProps: (record: IOperationRuleAlertUser) => ({
      disabled:
        !hasAnyChannelEnabled(record) && record.userId !== currentUserId,
    }),
  };

  const columns = createColumns({ t, currentUserId });

  const handleSelectAll = (checked: boolean) => {
    setCheckAll(checked);
    if (checked) {
      const allKeys = allUsersWithWhatsApp?.list?.map((user) => user?.userId);
      onSelectionChange(allKeys, allUsersWithWhatsApp?.list);

      return;
    }

    onSelectionChange([], []);
  };

  return {
    rowSelection,
    columns,
    searchTerm,
    setSearchTerm,
    handleSelectAll,
    allUsersWithWhatsApp,
    checkAll,
    filteredUsers,
  };
};
